/**
 * SearchInput — toolband search box.
 * Leading search icon + transparent input + optional trailing keyboard hint.
 * The whole element behaves like a single field; clicking the wrapper focuses
 * the input.
 * Source: .agents/design/project/artboard-radical.jsx:144-156
 */

import type { InputHTMLAttributes, Ref } from 'react';
import { Icon } from './Icon';
import { Kbd } from './Kbd';

interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * Trailing keyboard hint (e.g. ⌘K). Defaults to none — pass an explicit value
   * only when the input is actually wired to that shortcut. The global ⌘K
   * palette is a separate Modal-based search; advertising ⌘K next to a local
   * list filter is misleading because the two have different scopes.
   */
  kbd?: React.ReactNode;
  /** Wrapper className for layout (flex, max-width, etc.). */
  wrapperClassName?: string;
  ref?: Ref<HTMLInputElement>;
}

export function SearchInput({
  kbd = null,
  wrapperClassName = '',
  className = '',
  placeholder = 'Hledat…',
  ref,
  ...rest
}: SearchInputProps) {
  return (
    <label
      className={
        'flex items-center gap-2.5 border border-line bg-bg px-3 py-2 text-ink focus-within:border-ink ' +
        wrapperClassName
      }
    >
      <Icon name="search" size={14} className="text-ink-3" />
      <input
        {...rest}
        ref={ref}
        type="search"
        placeholder={placeholder}
        className={
          'min-w-0 flex-1 border-0 bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-3 ' +
          className
        }
      />
      {kbd != null && <Kbd>{kbd}</Kbd>}
    </label>
  );
}
